import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Plus, Pencil, Trash2, Activity, Ruler, IndianRupee, MapPin, User, AlertTriangle, ClipboardList, CheckCircle2, TrendingUp } from "lucide-react";import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatINRFull, formatArea, formatDate } from "@/lib/format";
import { PHASE_STATUS_STYLES } from "@/lib/theme";
import { StatusBadge } from "@/components/StatusBadge";
import { ProgressBar } from "@/components/ProgressBar";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { PhaseFormDialog } from "@/components/PhaseFormDialog";
import { ProgressDialog } from "@/components/ProgressDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { QuotationPanel } from "@/components/QuotationPanel";
import { SiteVisitsPanel } from "@/components/SiteVisitsPanel";
import { BillingPanel } from "@/components/BillingPanel";
import { PaymentsPanel } from "@/components/PaymentsPanel";
import { ProfitabilityPanel } from "@/components/ProfitabilityPanel";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { meta, user } = useAuth();
  const [project, setProject] = useState(null);
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [editPhase, setEditPhase] = useState(null);
  const [progressPhase, setProgressPhase] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}`); setProject(data); }
    catch (e) { if ([403, 404].includes(e.response?.status)) navigate("/projects"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => { await api.put(`/projects/${id}`, { status }); toast.success(`Status: ${status}`); load(); };
  const deletePhase = async () => { await api.delete(`/phases/${confirm.id}`); toast.success("Phase deleted"); setConfirm(null); load(); };
  const deleteProject = async () => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      setDeleteOpen(false);
      navigate("/projects");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete project");
    }
  };

  const canDelete = project && (["ADMIN", "SALES_MANAGER"].includes(user?.role) || project.created_by === user?.id);
  const isAccounts = user?.role === "ACCOUNTS";
  const canEditProject = !isAccounts;

  if (!project) return <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-40 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;
  const pr = project.progress;
  const fin = project.financials || { invoiced_total: 0, received_total: 0 };

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /> Back to Projects</button>

      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">{project.name}</h1>
              <StatusBadge value={project.status} type="project" dot />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
              <span className="font-mono text-xs">{project.project_code}</span>
              <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{project.company_name}</span>
              {project.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{project.location}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEditProject ? (
              <Select value={project.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="project-status-select" className="h-9 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.project_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <StatusBadge value={project.status} type="project" />
            )}
            {canDelete && canEditProject && (
              <Button data-testid="delete-project-btn" variant="outline" onClick={() => setDeleteOpen(true)}
                className="h-9 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            )}
          </div>
        </div>

        {pr.warnings.length > 0 && (
          <div className="mt-4 space-y-1.5">
            {pr.warnings.map((w, i) => (
              <div key={i} data-testid="project-warning" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"><AlertTriangle className="h-4 w-4 shrink-0" />{w}</div>
            ))}
          </div>
        )}

        {/* KPI cards */}
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-7">
          <Kpi label="Contracted Area" value={formatArea(pr.contracted_area)} icon={Ruler} />
          <Kpi label="Completed" value={formatArea(pr.completed_area)} icon={CheckCircle2} color="#059669" />
          <Kpi label="Remaining" value={formatArea(pr.remaining_area)} icon={ClipboardList} color="#EA580C" />
          <Kpi label="Completion" value={`${pr.completion_pct}%`} icon={TrendingUp} color="#0057A8" />
          <Kpi label="Quoted Value" value={formatINR(project.quoted_value)} icon={IndianRupee} />
          <Kpi label="Invoiced" value={formatINR(fin.invoiced_total)} icon={IndianRupee} color="#0057A8" />
          <Kpi label="Received" value={formatINR(fin.received_total)} icon={IndianRupee} color="#059669" />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="h-auto flex-wrap bg-white border border-slate-200 p-1">
          <TabsTrigger value="overview" data-testid="ptab-overview">Overview</TabsTrigger>
          <TabsTrigger value="execution" data-testid="ptab-execution">Execution</TabsTrigger>
          <TabsTrigger value="progress" data-testid="ptab-progress">Work Progress</TabsTrigger>
          <TabsTrigger value="quotation" data-testid="ptab-quotation">Quotation</TabsTrigger>
          <TabsTrigger value="documents" data-testid="ptab-documents">Documents</TabsTrigger>
          <TabsTrigger value="sitevisits" data-testid="ptab-sitevisits">Site Visits</TabsTrigger>
          <TabsTrigger value="billing" data-testid="ptab-billing">Billing</TabsTrigger>
          <TabsTrigger value="payments" data-testid="ptab-payments">Payments</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="ptab-profitability">Profitability</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Project Progress</h3>
              <span className="font-mono text-sm font-semibold text-slate-700">{formatArea(pr.completed_area)} / {formatArea(pr.contracted_area)} · {pr.completion_pct}%</span>
            </div>
            <ProgressBar height="h-4" segments={[
              { pct: (pr.completed_area / (pr.contracted_area || 1)) * 100, color: "#059669", label: "Completed" },
              { pct: (pr.in_progress_area / (pr.contracted_area || 1)) * 100, color: "#EA580C", label: "In Progress" },
            ]} />
            <div className="mt-3 flex flex-wrap gap-4 text-xs">
              <Legend color="#059669" label={`Completed · ${formatArea(pr.completed_area)}`} />
              <Legend color="#EA580C" label={`In Progress · ${formatArea(pr.in_progress_area)}`} />
              <Legend color="#E2E8F0" label={`Remaining · ${formatArea(pr.remaining_area)}`} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <InfoCard title="Commercial" items={[["Contracted Area", formatArea(pr.contracted_area)], ["Quoted Rate", project.quoted_rate ? `₹${project.quoted_rate}/Sq.Ft` : "—"], ["Quoted Value", formatINRFull(project.quoted_value)], ["Flooring System", project.flooring_system]]} />
            <InfoCard title="Project Details" items={[["Company", project.company_name], ["Contact", project.contact_person], ["Salesperson", project.assigned_salesperson_name], ["Expected Start", project.expected_start_date ? formatDate(project.expected_start_date) : "—"], ["Expected Completion", project.expected_completion_date ? formatDate(project.expected_completion_date) : "—"]]} />
          </div>
        </TabsContent>

        <TabsContent value="execution" className="mt-4">
          <ExecutionTab project={project} canEdit={canEditProject} onAdd={() => { setEditPhase(null); setPhaseOpen(true); }} onEdit={(ph) => { setEditPhase(ph); setPhaseOpen(true); }} onProgress={(ph) => setProgressPhase(ph)} onDelete={(ph) => setConfirm(ph)} />
        </TabsContent>

        <TabsContent value="progress" className="mt-4"><WorkProgressTab phases={project.phases} /></TabsContent>
        <TabsContent value="quotation" className="mt-4"><QuotationPanel projectId={project.id} /></TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentsPanel entityType="project" entityId={project.id} /></TabsContent>
        <TabsContent value="sitevisits" className="mt-4"><SiteVisitsPanel projectId={project.id} /></TabsContent>
        <TabsContent value="billing" className="mt-4"><BillingPanel projectId={project.id} quotedValue={project.quoted_value} onChanged={load} /></TabsContent>
        <TabsContent value="payments" className="mt-4"><PaymentsPanel projectId={project.id} onChanged={load} /></TabsContent>
        <TabsContent value="profitability" className="mt-4"><ProfitabilityPanel projectId={project.id} onChanged={load} /></TabsContent>
      </Tabs>

      <PhaseFormDialog open={phaseOpen} onOpenChange={setPhaseOpen} projectId={project.id} phase={editPhase} onSaved={load} />
      <ProgressDialog open={!!progressPhase} onOpenChange={(o) => !o && setProgressPhase(null)} phase={progressPhase} onSaved={load} />
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} title="Delete phase?" description="This removes the phase and its work progress history." confirmText="Delete" onConfirm={deletePhase} />
      <ConfirmDialog open={deleteOpen} onOpenChange={setDeleteOpen} title="Delete this project?" description={`This permanently deletes "${project.name}" along with its phases, work progress, quotation and project documents. The originating lead will be reverted so you can re-convert it if needed. This cannot be undone.`} confirmText="Delete Project" onConfirm={deleteProject} />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color = "#102A43", soon }) {
  return (
    <div className={`rounded-lg border border-slate-100 p-3 ${soon ? "bg-slate-50/40" : "bg-slate-50/60"}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}{soon && <span className="rounded bg-slate-200 px-1 text-[8px] text-slate-500">Soon</span>}</div>
      <div className="mt-1 font-mono text-base font-bold" style={{ color: soon ? "#94A3B8" : color }}>{value}</div>
    </div>
  );
}

function Legend({ color, label }) {
  return <span className="flex items-center gap-1.5 text-slate-500"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />{label}</span>;
}

function InfoCard({ title, items }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="mb-3 text-sm font-semibold text-slate-800">{title}</h3>
      <div className="space-y-2">
        {items.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm"><span className="text-slate-400">{k}</span><span className="font-medium text-slate-700">{v}</span></div>
        ))}
      </div>
    </div>
  );
}

function ExecutionTab({ project, onAdd, onEdit, onProgress, onDelete, canEdit = true }) {
  const pr = project.progress;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Total Area" value={formatArea(pr.contracted_area)} icon={Ruler} />
        <Kpi label="Completed" value={formatArea(pr.completed_area)} icon={CheckCircle2} color="#059669" />
        <Kpi label="In Progress" value={formatArea(pr.in_progress_area)} icon={Activity} color="#EA580C" />
        <Kpi label="Remaining" value={formatArea(pr.remaining_area)} icon={ClipboardList} />
        <Kpi label="Completion" value={`${pr.completion_pct}%`} icon={TrendingUp} color="#0057A8" />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">Execution Phases</h3>
        {canEdit && <Button data-testid="add-phase-btn" onClick={onAdd} size="sm" className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Add Phase</Button>}
      </div>

      {(!project.phases || project.phases.length === 0) ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center">
          <p className="text-sm font-semibold text-slate-700">No execution phases yet</p>
          <p className="mt-1 text-xs text-slate-400">{canEdit ? "Break the contracted area into phases to track site execution." : "Site team hasn't added phases yet."}</p>
          {canEdit && <Button onClick={onAdd} className="mt-4 gap-1.5"><Plus className="h-4 w-4" /> Add First Phase</Button>}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Phase</th><th className="px-4 py-3 text-right">Planned</th><th className="px-4 py-3 text-right">Completed</th><th className="px-4 py-3 text-right">Remaining</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Engineer</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {project.phases.map((ph) => {
                  const rem = Math.max((ph.planned_area || 0) - (ph.completed_area || 0), 0);
                  return (
                    <tr key={ph.id} data-testid={`phase-row-${ph.id}`} className="group border-b border-slate-50">
                      <td className="px-4 py-3"><div className="font-semibold text-slate-800">{ph.name}</div><div className="text-xs text-slate-400">Phase {ph.phase_number}{ph.planned_start_date ? ` · ${formatDate(ph.planned_start_date)}` : ""}</div></td>
                      <td className="px-4 py-3 text-right font-mono text-slate-700">{formatArea(ph.planned_area || 0)}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{formatArea(ph.completed_area || 0)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500">{formatArea(rem)}</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-1"><StatusBadge value={ph.status} type="phase" dot />{(ph.completed_area || 0) > (ph.planned_area || 0) && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">Over</span>}</div></td>
                      <td className="px-4 py-3 text-xs text-slate-600">{ph.assigned_engineer_name || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => onProgress(ph)} data-testid={`update-progress-${ph.id}`} className="h-7 gap-1 text-xs"><TrendingUp className="h-3 w-3" /> Progress</Button>
                          <button onClick={() => onEdit(ph)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => onDelete(ph)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {project.phases.map((ph) => {
              const rem = Math.max((ph.planned_area || 0) - (ph.completed_area || 0), 0);
              const pct = ph.planned_area ? Math.round((ph.completed_area || 0) / ph.planned_area * 100) : 0;
              return (
                <div key={ph.id} data-testid={`phase-card-${ph.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div><div className="font-semibold text-slate-800">{ph.name}</div><div className="text-xs text-slate-400">Phase {ph.phase_number}</div></div>
                    <div className="flex items-center gap-1"><StatusBadge value={ph.status} type="phase" dot />{(ph.completed_area || 0) > (ph.planned_area || 0) && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">Over</span>}</div>
                  </div>
                  <div className="mt-3"><div className="mb-1 flex justify-between text-xs font-mono text-slate-600"><span>{formatArea(ph.completed_area || 0)} / {formatArea(ph.planned_area || 0)}</span><span>{pct}%</span></div><ProgressBar pct={pct} /></div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-500">{ph.assigned_engineer_name || "Unassigned"} · {formatArea(rem)} left</span>
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => onProgress(ph)} className="h-8 gap-1 text-xs font-semibold"><TrendingUp className="h-3.5 w-3.5" /> Update</Button>
                      <button onClick={() => onEdit(ph)} className="rounded-md border border-slate-200 p-1.5 text-slate-500"><Pencil className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function WorkProgressTab({ phases }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      const all = [];
      for (const ph of phases || []) {
        try { const { data } = await api.get(`/phases/${ph.id}/progress`); data.forEach((e) => all.push({ ...e, phase_name: ph.name })); } catch (e) { /* */ }
      }
      all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setEntries(all); setLoading(false);
    })();
  }, [phases]);

  if (loading) return <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>;
  if (entries.length === 0) return <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center"><p className="text-sm font-semibold text-slate-700">No work progress recorded yet</p><p className="mt-1 text-xs text-slate-400">Use "Update Progress" on a phase in the Execution tab to log daily site work.</p></div>;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <ol className="relative space-y-4 border-l border-slate-200 pl-6">
        {entries.map((e) => (
          <li key={e.id} className="relative">
            <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-[#0057A8] ring-4 ring-white"><Activity className="h-3.5 w-3.5" /></span>
            <div className="flex flex-wrap items-center justify-between gap-1">
              <p className="text-sm font-semibold text-slate-800">{formatArea(e.area_completed)} · {e.phase_name}</p>
              <span className="text-xs text-slate-400">{formatDate(e.date)}</span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{[e.zone, e.description, e.labour && `Labour: ${e.labour}`].filter(Boolean).join(" · ") || "Work recorded"}</p>
            <p className="mt-0.5 text-[11px] text-slate-400">By {e.submitted_by_name}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}
